import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, RefreshCw, Filter } from "lucide-react";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import { GenericView, SearchInput } from "../../components/common/table";
import FilterAwarenessBar from "../../components/common/AdvancedFilter/FilterAwarenessBar";
import { AppSelect } from '../../components/common/select';
import DepartmentPositionSection from "./Department/Component/DepartmentPositionSection";
import AdvancedFilterPanel from "../../components/common/AdvancedFilter";
import {
  DEFAULT_PERIOD_STATE,
  getActiveFilterCount,
  getActivePeriodRange,
} from "../../components/common/AdvancedFilter/periodUtils";
import { ActionButtons, ColumnToggle } from "../../components/common/table";
import "./HRISListPage.css";
import { useAuth } from "../../contexts/AuthProvider";

const BRAND = "#319EA9";
const DEFAULT_CURRENCY = "Rp";
const formatCurrency = (value) => {
  if (!value) return "";

  return value
    .toString()
    .replace(/\D/g, "")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const StatusBadge = ({ value }) => {
  if (!value) return <span className="text-muted">—</span>;
  const v = String(value).toLowerCase();
  const map = {
    approved: "badge badge-outline text-lime",
    active: "bg-success",
    present: "bg-success",
    open: "bg-success",
    completed: "bg-success",
    rejected: "bg-danger",
    inactive: "bg-danger",
    absent: "bg-danger",
    cancelled: "bg-danger",
    pending: "bg-warning text-dark",
    draft: "bg-warning text-dark",
    submitted: "bg-warning text-dark",
    "half day": "bg-azure-lt text-azure",
    "on leave": "bg-purple-lt text-purple",
    closed: "bg-secondary",
    resigned: "bg-secondary",
  };
  const cls = map[v] || "bg-azure-lt text-azure";
  return <span className={`badge ${cls}`}>{value}</span>;
};

const HRISListPage = ({
  title,
  description,
  icon,
  iconColor = BRAND,
  columns,
  fetchData,
  createData,
  updateData,
  deleteData,
  formFields = [],
  filterFields = [],
  entityName = "Record",
  searchPlaceholder = "Search...",
  canCreate = true,
  canEdit = true,
  canDelete = true,
  fetchById,
  warnOnLocalDraft = false,
  extraActions,
  headerContent,
  expandableRows = false,
  renderExpandedRow,
  departmentPositionSection,
  onExportExcel,
  onExportPDF,
  moduleKey = null,
}) => {
  const { hasRole, can } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [search, setSearch] = useState("");

  const [appliedSearch, setAppliedSearch] = useState("");

  const [filterData, setFilterData] = useState({
    ...DEFAULT_PERIOD_STATE,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    ...DEFAULT_PERIOD_STATE,
  });

  const activeCount = getActiveFilterCount(appliedFilters, filterFields);

  const totalActiveCount = activeCount + (appliedSearch ? 1 : 0);

  const [showForm, setShowForm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [jobTitles, setJobTitles] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loaders = formFields.filter(
      (f) => typeof f.optionsLoader === "function",
    );
    if (loaders.length === 0) return;
    loaders.forEach(async (f) => {
      try {
        const data = await f.optionsLoader();
        const opts = Array.isArray(data)
          ? data.map((e) => {
              if (typeof e === "string" || typeof e === "number") {
                return { value: e, label: String(e) };
              }
              return {
                value:
                  e.value ?? e.id ?? e.code ?? e.name ?? e.label ?? String(e),
                label: e.label ?? e.name ?? e.fullName ?? e.code ?? String(e),
              };
            })
          : [];
        setDynamicOptions((prev) => ({ ...prev, [f.key]: opts }));
      } catch (_) {}
    });
  }, []);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const result = await fetchData({
        page,
        size: pageSize,
        search: appliedSearch,
        ...appliedFilters,
      });

      setRecords(result.content || []);
      setTotalElements(result.totalElements || 0);

      if (warnOnLocalDraft && result._source === "local-draft") {
        showAlert(
          "warning",
          "Showing offline draft data — could not reach the server.",
        );
      }
    } catch {
      setRecords([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [fetchData, page, pageSize, appliedSearch, appliedFilters, warnOnLocalDraft, showAlert]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = () => {
    setPage(0);
    setAppliedSearch(search);
  };

  const handleClearSearch = () => {
    setSearch("");
    setAppliedSearch("");
    setPage(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleFilterChange = (key, value) => {
    setFilterData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFilterApply = () => {
    const periodRange = getActivePeriodRange(filterData);

    const resolved = {
      ...filterData,
      startDate: periodRange.startDate,
      endDate: periodRange.endDate,
    };

    setAppliedFilters(resolved);
    setPage(0);
  };

  const handleFilterReset = () => {
    setFilterData({
      ...DEFAULT_PERIOD_STATE,
    });

    setAppliedFilters({
      ...DEFAULT_PERIOD_STATE,
    });

    setPage(0);
  };

  const openAdd = () => {
    const init = {};
    formFields.forEach((f) => {
      init[f.key] = f.defaultValue ?? "";
    });
    setFormData(init);
    setSelectedRecord(null);
    setJobTitles([]); // reset job title
    setErrors({});
    setShowForm(true);
  };

  const openEdit = async (record) => {
    let source = record;
    if (fetchById) {
      try {
        source = await fetchById(record.id);
      } catch (_) {
        source = record;
      }
    }
    const init = {};
    formFields.forEach((f) => {
      init[f.key] = source[f.key] ?? f.defaultValue ?? "";
    });
    setFormData(init);
    setJobTitles(source.jobTitles || []);
    setSelectedRecord(record);
    setErrors({});
    setShowForm(true);
  };

  const handleSave = async () => {
    const newErrors = {};

    formFields.forEach((field) => {
      if (
        (field.required || (field.requiredOnCreate && !selectedRecord)) &&
        (!formData[field.key] || String(formData[field.key]).trim() === "")
      ) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...formData,
        jobTitles,
      };

      if (selectedRecord) {
        await updateData(selectedRecord.id, payload);
        showAlert("success", `${entityName} updated.`);
      } else {
        await createData(payload);
        showAlert("success", `${entityName} created.`);
      }

      setShowForm(false);
      load();
    } catch (err) {
      showAlert(
        "danger",
        `Failed to save ${entityName}. ${err?.response?.data?.message || ""}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await deleteData(itemToDelete.id);
      showAlert("success", `${entityName} deleted.`);
      load();
    } catch {
      showAlert("danger", `Failed to delete ${entityName}.`);
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const tableData = useMemo(
    () =>
      records.map((record, idx) => ({
        ...record,
        rowNumber: page * pageSize + idx + 1,
      })),
    [records, page, pageSize],
  );

  const tableColumns = useMemo(
    () => [
      {
        key: "rowNumber",
        label: "No",
        width: "64px",
        sortable: false,
        className: "text-muted",
      },
      ...columns.map((col) => ({
        ...col,
        render: (value, record) => {
          if (col.render) return col.render(value, record);
          if (col.badge) return <StatusBadge value={value} />;
          return value ?? <span className="text-muted">—</span>;
        },
      })),
    ],
    [columns],
  );

  const defaultVisibleColumns = useMemo(
    () => tableColumns.map((c) => c.key),
    [tableColumns],
  );

  const [visibleColumns, setVisibleColumns] = useState([]);

  const canDo = (capability) => !moduleKey || hasRole("ROLE_ADMIN") || hasRole("ROLE_DIRECTOR") || can(capability, moduleKey);
  const allowCreate = canCreate && canDo("create");
  const allowEdit = canEdit && canDo("edit");
  const allowDelete = canDelete && canDo("delete");

  useEffect(() => {
    setVisibleColumns(defaultVisibleColumns);
  }, [defaultVisibleColumns]);

  const handleToggleColumn = (key) => {
    setVisibleColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleToggleAllColumns = () => {
    if (visibleColumns.length === tableColumns.length) {
      setVisibleColumns(["rowNumber"]);
    } else {
      setVisibleColumns(tableColumns.map((c) => c.key));
    }
  };

  return (
    <div className="hris-list-page">
      {/* Alert */}
      {alert && (
        <div className={`alert alert-${alert.type} alert-dismissible mb-3`}>
          {alert.message}
          <button
            type="button"
            className="btn-close"
            onClick={() => setAlert(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div className="page-header mb-3">
        <div className="row align-items-center justify-content-between">
          <div className="col">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 44, height: 44, backgroundColor: iconColor }}
              >
                {React.cloneElement(icon, { size: 20, color: "#fff" })}
              </div>

              <div>
                <h2 className="page-title mb-0">{title}</h2>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  {description}
                </p>
              </div>
            </div>
          </div>

          <div className="col-auto">
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary"
                style={{
                  padding: "12px",
                  borderRadius: "4px",
                  border: "1px solid #319EA9",
                  color: "#319EA9",
                }}
                onClick={load}
              >
                <RefreshCw size={16} className="me-1" />
                Refresh
              </button>
              {allowCreate && (
                <button
                  className="btn text-white fw-medium"
                  style={{
                    backgroundColor: "#319EA9",
                    padding: "12px",
                    borderRadius: "4px",
                    border: "1px",
                    boxShadow: "0 2px 8px rgba(31,78,121,0.2)",
                  }}
                  onClick={openAdd}
                >
                  <Plus size={16} className="me-2" />
                  Add {entityName}
                </button>
              )}

              {headerContent}
            </div>
          </div>
        </div>
      </div>

      <div className="card hris-list-card">
        <div className="card-body">
          <div className="hris-list-heading">
            <div>
              <h3>{title} List</h3>
              <p>
                {totalElements} {totalElements === 1 ? "record" : "records"} available
              </p>
            </div>
          </div>

          <GenericView
            data={tableData}
            columns={tableColumns}
            isLoading={loading}
            expandableRows={expandableRows}
            renderExpandedRow={renderExpandedRow}
            onEdit={allowEdit ? openEdit : undefined}
            visibleColumns={visibleColumns}
            onVisibleColumnsChange={setVisibleColumns}
            onDelete={
              allowDelete
                ? (record) => {
                    setItemToDelete(record);
                    setShowDeleteModal(true);
                  }
                : undefined
            }
            customRowActions={extraActions}
            checkboxSelection={true}
            uniqueId={`hris-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            emptyStateMessage={`No ${entityName.toLowerCase()} records found${appliedSearch ? ". Try clearing your search." : "."}`}
            topControlsContent={
              <div className="hris-table-toolbar">
                <button
                  type="button"
                  className="btn btn-outline-secondary hris-filter-button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasExpense"
                >
                  <Filter size={16} className="me-1" />
                  Filter
                  {activeCount > 0 && (
                    <span className="badge bg-primary ms-1">{activeCount}</span>
                  )}
                </button>

                <div className="hris-table-actions">
                  <SearchInput
                    value={search}
                    onChange={setSearch}
                    onKeyPress={handleKeyDown}
                    onSearchSubmit={handleSearch}
                    placeholder={searchPlaceholder}
                  />

                  <ColumnToggle
                    columns={tableColumns}
                    visibleColumns={visibleColumns}
                    onToggleColumn={handleToggleColumn}
                    onToggleAll={handleToggleAllColumns}
                  />

                  <ActionButtons
                    uniqueId={`hris-${title}`}
                    onExportExcel={
                      onExportExcel
                        ? () =>
                            onExportExcel({
                              search: appliedSearch,
                              ...appliedFilters,
                            })
                        : undefined
                    }
                    onExportPDF={
                      onExportPDF
                        ? () =>
                            onExportPDF({
                              search: appliedSearch,
                              ...appliedFilters,
                            })
                        : undefined
                    }
                  />
                </div>
              </div>
            }
            belowTopControlsContent={
              <FilterAwarenessBar
                title={`${title} is showing a filtered view`}
                summary={`Filtered by ${totalActiveCount} ${
                  totalActiveCount === 1 ? "criterion" : "criteria"
                }`}
                detail={`Search: ${appliedSearch}`}
                loading={loading}
                filterTags={
                  appliedSearch
                    ? [{ key: "search", label: `Search: ${appliedSearch}` }]
                    : []
                }
                onRemoveFilter={handleClearSearch}
                onResetFilters={handleClearSearch}
              />
            }
            pagination={{
              currentPage: page + 1,
              totalRows: totalElements,
              rowsPerPage: pageSize,
              onPageChange: (newPage) => setPage(newPage - 1),
              onRowsPerPageChange: (newSize) => {
                setPageSize(newSize);
                setPage(0);
              },
            }}
          />
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <>
          {/* Backdrop or overlay */}
          <div
            onClick={() => setShowForm(false)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.35)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              zIndex: 1040,
            }}
          />

          {/* Modal */}
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
              role="document"
            >
              <div className="modal-content border-0 shadow-lg">
                {/* Header */}
                <div className="modal-header border-bottom py-3 px-4">
                  <h5 className="modal-title mb-0 fw-semibold">
                    {selectedRecord
                      ? `Edit ${entityName}`
                      : `Add ${entityName}`}
                  </h5>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowForm(false)}
                  />
                </div>

                {/* Body */}
                <div
                  className="modal-body p-4"
                  style={{ maxHeight: "min(70vh, 760px)", overflowY: "auto" }}
                >
                  <div className="row g-3">
                    {formFields.map((field) => {
                      const resolvedOptions = field.optionsLoader
                        ? dynamicOptions[field.key] || []
                        : field.options || [];

                      if (field.type === "divider") {
                        return (
                          <div
                            key={field.key}
                            className="col-12"
                            style={{
                              height: "1px",
                              background: "#DCE1E7",
                              margin: "16px 0",
                            }}
                          />
                        );
                      }
                      if (field.type === "section") {
                        return (
                          <div className="col-12" key={field.key}>
                            <div className="d-flex align-items-center my-2 mb-2">
                              <div
                                style={{
                                  flex: 1,
                                  height: "1px",
                                  backgroundColor: "#dfe3e8",
                                }}
                              />

                              <span
                                className="px-3 fw-semibold"
                                style={{
                                  color: "#1f2937",
                                  fontSize: "15px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {field.label}
                              </span>

                              <div
                                style={{
                                  flex: 1,
                                  height: "1px",
                                  backgroundColor: "#dfe3e8",
                                }}
                              />
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          className={
                            field.layout === "half"
                              ? "col-md-6 col-12"
                              : "col-12"
                          }
                          key={field.key}
                        >
                          <label className="form-label small fw-medium">
                            {field.label}
                            {field.required && (
                              <span className="text-danger ms-1">*</span>
                            )}
                          </label>

                          {field.type === "select" ? (
                            <>
                              <AppSelect
                                name={field.key}
                                value={formData[field.key] || ""}
                                options={resolvedOptions.map((opt) => ({
                                  id: opt.value ?? opt,
                                  name: opt.label ?? opt,
                                }))}
                                placeholder={`Select ${field.label}`}
                                dropdownParent="body"
                                onChange={(name, value) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    [name]: value,
                                  }));

                                  setErrors((prev) => ({
                                    ...prev,
                                    [name]: "",
                                  }));
                                }}
                              />

                              {errors[field.key] && (
                                <div
                                  className="text-danger mt-1"
                                  style={{ fontSize: "12px" }}
                                >
                                  {errors[field.key]}
                                </div>
                              )}
                            </>
                          ) : field.type === "textarea" ? (
                            <>
                              <textarea
                                className={`form-control ${
                                  errors[field.key] ? "is-invalid" : ""
                                }`}
                                rows={4}
                                placeholder={field.placeholder || ""}
                                value={formData[field.key] || ""}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    [field.key]: e.target.value,
                                  }));

                                  setErrors((prev) => ({
                                    ...prev,
                                    [field.key]: "",
                                  }));
                                }}
                              />

                              {errors[field.key] && (
                                <div className="invalid-feedback d-block">
                                  {errors[field.key]}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {field.type === "currency" ? (
                                <div className="input-group">
                                  <span className="input-group-text">
                                    {field.currency || DEFAULT_CURRENCY}
                                  </span>

                                  <input
                                    type="text"
                                    className={`form-control ${
                                      errors[field.key] ? "is-invalid" : ""
                                    }`}
                                    placeholder={field.placeholder || ""}
                                    value={formatCurrency(formData[field.key])}
                                    onChange={(e) => {
                                      const raw = e.target.value.replace(
                                        /\D/g,
                                        "",
                                      );

                                      setFormData((prev) => ({
                                        ...prev,
                                        [field.key]: raw,
                                      }));

                                      setErrors((prev) => ({
                                        ...prev,
                                        [field.key]: "",
                                      }));
                                    }}
                                  />
                                </div>
                              ) : (
                                <input
                                  type={field.type || "text"}
                                  className={`form-control ${
                                    errors[field.key] ? "is-invalid" : ""
                                  }`}
                                  placeholder={field.placeholder || ""}
                                  value={formData[field.key] || ""}
                                  onChange={(e) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      [field.key]: e.target.value,
                                    }));

                                    setErrors((prev) => ({
                                      ...prev,
                                      [field.key]: "",
                                    }));
                                  }}
                                />
                              )}

                              {errors[field.key] && (
                                <div className="invalid-feedback d-block">
                                  {errors[field.key]}
                                </div>
                              )}
                            </>
                          )}

                          {field.hint && (
                            <div className="form-text">{field.hint}</div>
                          )}
                        </div>
                      );
                    })}

                    {entityName === "Department" && (
                      <DepartmentPositionSection
                        jobTitles={jobTitles}
                        setJobTitles={setJobTitles}
                      />
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div
                  className="modal-footer border-top d-flex justify-content-between"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(31,101,108,0.08) 0%, rgba(49,158,169,0.04) 100%)",
                  }}
                >
                  <button
                    type="button"
                    className="btn"
                    style={{
                      color: "#6c7a91",
                      border: "none",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    }}
                    onClick={() => setShowForm(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn text-white"
                    style={{
                      backgroundColor: iconColor,
                      borderColor: iconColor,
                    }}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving
                      ? "Saving..."
                      : selectedRecord
                        ? "Update"
                        : "Create"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        title={`Delete ${entityName}`}
        message={`Are you sure you want to delete this ${entityName.toLowerCase()}? This action cannot be undone.`}
        onConfirm={handleDelete}
        confirmText="Delete"
        confirmVariant="danger"
      />

      <AdvancedFilterPanel
        offcanvasId="offcanvasExpense"
        title={`Filter ${title}`}
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={filterFields}
        showPeriod={true}
      />
    </div>
  );
};

export default HRISListPage;
